// Presentational toast card. Generic — knows nothing about the game. It reads
// per-state config (icon, default title, a11y role, accent, duration) from
// toastConstants.js and renders icon + title + sub + optional amount/meta +
// dismiss. The only inline styles are runtime values: the accent (the single
// colour source of truth, fed to the SCSS via --toast-accent) and the
// countdown duration. Tap or swipe also dismisses.
import { useRef } from 'react'
import toast from 'react-hot-toast'
import { TOAST_STATES, TOAST_TYPES } from '@constants/toastConstants'
import ToastIcon from './ToastIcon'

const SWIPE_DISMISS_PX = 110

const Toast = ({ t, type, options = {} }) => {
  const cfg = TOAST_STATES[type] ?? TOAST_STATES[TOAST_TYPES.INFO]
  const ref = useRef(null)
  const drag = useRef({ startX: 0, dx: 0, moved: false })

  const title = options.title ?? cfg.title
  const sub = options.sub
  const amount = options.amount
  const meta = options.meta
  const duration = options.duration ?? cfg.duration
  const isLoading = type === TOAST_TYPES.LOADING
  const showBar = !isLoading && Number.isFinite(duration)

  // ---- swipe to dismiss (pointer-based, works on touch + mouse) ----
  const onPointerDown = (e) => {
    const el = ref.current
    if (!el) return
    drag.current = { startX: e.clientX, dx: 0, moved: false }
    el.style.transition = 'none'

    const move = (ev) => {
      const dx = ev.clientX - drag.current.startX
      drag.current.dx = dx
      if (Math.abs(dx) > 5) drag.current.moved = true
      el.style.transform = `translateX(${dx}px)`
      el.style.opacity = String(Math.max(0.15, 1 - Math.abs(dx) / 200))
    }

    const up = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      const dx = drag.current.dx
      if (Math.abs(dx) > SWIPE_DISMISS_PX) {
        el.style.transition = 'transform .2s ease, opacity .2s ease'
        el.style.transform = `translateX(${dx > 0 ? 460 : -460}px)`
        el.style.opacity = '0'
        setTimeout(() => toast.dismiss(t.id), 180)
      } else {
        el.style.transition = 'transform .25s cubic-bezier(.2,.9,.25,1), opacity .2s ease'
        el.style.transform = ''
        el.style.opacity = ''
      }
    }

    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }

  const onClick = () => {
    if (drag.current.moved) {
      drag.current.moved = false
      return
    }
    toast.dismiss(t.id)
  }

  const style = { '--toast-accent': cfg.accent }
  if (showBar) style['--toast-duration'] = `${duration}ms`

  return (
    <div
      ref={ref}
      className={`toast toast--${type}${t.visible ? ' is-visible' : ''}`}
      style={style}
      role={cfg.role}
      aria-live={cfg.ariaLive}
      onPointerDown={onPointerDown}
      onClick={onClick}
    >
      <div className={`toast__icon${isLoading ? ' toast__icon--spin' : ''}`}>
        <ToastIcon iconKey={cfg.iconKey} size={22} />
      </div>

      <div className="toast__body">
        <div className="toast__title">{title}</div>
        {sub ? <div className="toast__sub">{sub}</div> : null}
      </div>

      {amount || meta ? (
        <div className="toast__meta-wrap">
          {amount ? <div className="toast__amount">{amount}</div> : null}
          {meta ? <div className="toast__meta">{meta}</div> : null}
        </div>
      ) : null}

      {options.action ? (
        <button
          type="button"
          className="toast__action"
          onClick={(e) => {
            e.stopPropagation()
            options.action.onClick?.()
            toast.dismiss(t.id)
          }}
        >
          {options.action.label}
        </button>
      ) : null}

      <button
        type="button"
        className="toast__dismiss"
        aria-label="Dismiss notification"
        onClick={(e) => {
          e.stopPropagation()
          toast.dismiss(t.id)
        }}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="M5 5 L15 15" />
          <path d="M15 5 L5 15" />
        </svg>
      </button>

      {showBar ? <div className="toast__bar" /> : null}
    </div>
  )
}

export default Toast
